import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Award, Star, Heart, ChevronLeft, ChevronRight } from 'lucide-react';

const Home = () => {
  const { t } = useTranslation();
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const videoRef = useRef(null);

  const testimonials = t('home.testimonials', { returnObjects: true }) || [];

  useEffect(() => {
    if (videoRef.current) {
      const playVideo = async () => {
        try {
          videoRef.current.muted = true;
          await videoRef.current.play();
        } catch (err) {
          setTimeout(() => {
            if (videoRef.current) videoRef.current.play().catch(() => {});
          }, 1000);
        }
      };
      if (videoRef.current.readyState >= 3) playVideo();
      else videoRef.current.addEventListener('loadeddata', playVideo);
    }
  }, []);

  useEffect(() => {
    if (!isPaused && testimonials.length > 0) {
      const interval = setInterval(() => {
        setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
      }, 3000);
      return () => clearInterval(interval);
    }
  }, [isPaused, testimonials.length]);

  const nextTestimonial = () => setCurrentTestimonial((p) => (p + 1) % testimonials.length);
  const prevTestimonial = () => setCurrentTestimonial((p) => (p - 1 + testimonials.length) % testimonials.length);

  const activeTestimonial = testimonials[currentTestimonial] || { text: '', author: '' };

  return (
    <div className="home-page">
      <section className="hero-section" style={{
        backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url('https://customer-assets.emergentagent.com/job_preview-live-18/artifacts/5u08343g_9d4ce7_585589fc5fcc4f2b94b39d8b3280755df000.avif')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}>
        <video
          ref={videoRef}
          muted
          loop
          playsInline
          autoPlay
          preload="metadata"
          poster="https://customer-assets.emergentagent.com/job_preview-live-18/artifacts/5u08343g_9d4ce7_585589fc5fcc4f2b94b39d8b3280755df000.avif"
          className="hero-video"
          crossOrigin="anonymous"
        >
          <source src="https://customer-assets.emergentagent.com/job_preview-live-18/artifacts/yr42itk4_videoo.mp4" type="video/mp4" />
        </video>
        <div className="hero-overlay"></div>
        <div className="hero-content">
          <h1 className="hero-title">{t('home.heroTitle')}</h1>
          <p className="hero-subtitle">{t('home.heroSubtitle')}</p>
        </div>
      </section>

      <section className="section why-me-section">
        <h2 className="section-title">{t('home.whyMeTitle')}</h2>
        <p className="section-subtitle">{t('home.whyMeSubtitle')}</p>

        <div className="awards-grid">
          <div className="award-card">
            <Award className="award-icon" size={48} />
            <h3>{t('home.award1Title')}</h3>
            <p>{t('home.award1Sub')}</p>
          </div>
          <div className="award-card">
            <Star className="award-icon" size={48} />
            <h3>{t('home.award2Title')}</h3>
            <p>{t('home.award2Sub')}</p>
          </div>
          <div className="award-card">
            <Heart className="award-icon" size={48} />
            <h3>{t('home.award3Title')}</h3>
            <p>{t('home.award3Sub')}</p>
          </div>
        </div>

        <div className="cta-buttons">
          <Link to="/chi-sono" className="btn btn-primary">{t('home.ctaChiSono')}</Link>
          <Link to="/gallery" className="btn btn-secondary">{t('home.ctaGallery')}</Link>
        </div>
      </section>

      <section className="section testimonial-section">
        <h2 className="section-title">{t('home.testimonialsTitle')}</h2>

        <div
          className="testimonial-carousel"
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
        >
          <button className="carousel-btn prev" onClick={prevTestimonial} aria-label="Previous" data-testid="testimonial-prev-btn">
            <ChevronLeft size={32} />
          </button>

          <div className="testimonial-card">
            <p className="testimonial-text">"{activeTestimonial.text}"</p>
            <p className="testimonial-author">— {activeTestimonial.author}</p>
          </div>

          <button className="carousel-btn next" onClick={nextTestimonial} aria-label="Next" data-testid="testimonial-next-btn">
            <ChevronRight size={32} />
          </button>

          <div className="carousel-dots">
            {testimonials.map((_, index) => (
              <button
                key={index}
                className={`dot ${index === currentTestimonial ? 'active' : ''}`}
                onClick={() => setCurrentTestimonial(index)}
                aria-label={`Testimonial ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </section>

      <section className="section services-section">
        <div className="services-grid">
          <div className="service-card" style={{
            backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url('https://images.pexels.com/photos/7959649/pexels-photo-7959649.jpeg')`,
          }}>
            <h3>{t('home.servicePerDonna')}</h3>
            <Link to="/servizi-offerti" className="btn btn-light">{t('home.scopriDiPiu')}</Link>
          </div>

          <div className="service-card" style={{
            backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url('https://customer-assets.emergentagent.com/job_preview-live-18/artifacts/zveq6fts_884b929a1a0de962a6ecbbe06d94dfde.jpg')`,
          }}>
            <h3>{t('home.servicePerCoppie')}</h3>
            <Link to="/servizi-offerti" className="btn btn-light">{t('home.scopriDiPiu')}</Link>
          </div>

          <div className="service-card" style={{
            backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url('https://images.unsplash.com/photo-1603394151492-5e9b974b090b')`,
          }}>
            <h3>{t('home.servicePerUomo')}</h3>
            <Link to="/servizi-offerti" className="btn btn-light">{t('home.scopriDiPiu')}</Link>
          </div>
        </div>
      </section>

      <section className="section luxury-cta" style={{
        backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url('https://images.unsplash.com/photo-1562281302-809108fd533c')`,
      }}>
        <h2>{t('home.luxuryTitle')}</h2>
        <Link to="/servizi-di-lusso" className="btn btn-primary">{t('home.luxuryCta')}</Link>
      </section>

      <section className="section privacy-section" style={{
        backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url('https://images.pexels.com/photos/15501246/pexels-photo-15501246.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')`,
      }}>
        <div className="privacy-content">
          <h2>{t('home.privacyTitle')}</h2>
          <p>{t('home.privacyText')}</p>
          <p className="signature">{t('home.signature')}</p>
        </div>
      </section>
    </div>
  );
};

export default Home;
